import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  AlertTriangle,
  ArrowLeft,
  Link2,
  ChevronRight,
  Mail,
  Phone,
  Building2,
  MapPin,
  UserCircle2,
  Tag,
  CalendarDays,
  FileText,
  Layers,
  Globe,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Human-friendly labels + icons for the conflict comparison view. */
const FIELD_META = {
  name: { label: "Name", icon: UserCircle2 },
  email: { label: "Email", icon: Mail },
  phone: { label: "Phone", icon: Phone },
  company: { label: "Company", icon: Building2 },
  location: { label: "Location", icon: MapPin },
  status: { label: "Status", icon: Tag },
  subscription_plan: { label: "Subscription Plan", icon: Tag },
  assigned_to: { label: "Assignee", icon: UserCircle2 },
  follow_up_date: { label: "Follow-up Date", icon: CalendarDays },
  notes: { label: "Notes", icon: FileText },
  integration_name: { label: "Integration", icon: Globe },
  source: { label: "Source", icon: Globe },
};
const MERGEABLE_FIELDS = Object.keys(FIELD_META);

function isEmpty(v) {
  return v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
}

function displayValue(field, value, teamMap) {
  if (isEmpty(value)) return <span className="text-gray-400 italic">empty</span>;
  if (field === "assigned_to") {
    return teamMap?.[value] || value;
  }
  if (field === "follow_up_date") {
    try {
      return format(new Date(value), "d MMMM yyyy");
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatCreatedAt(iso) {
  try {
    return format(new Date(iso), "d MMM yyyy, h:mm a");
  } catch {
    return iso || "";
  }
}

export default function MergeLeadDialog({ open, onOpenChange, currentLead, teamMembers, onMerged, presetSourceId = null }) {
  const [stage, setStage] = useState("select"); // select | resolve | confirm
  const [candidates, setCandidates] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [fieldChoices, setFieldChoices] = useState({}); // field -> "current" | "source"
  const [customFieldChoices, setCustomFieldChoices] = useState({}); // key -> "current" | "source"
  const [merging, setMerging] = useState(false);

  const teamMap = useMemo(() => {
    const m = {};
    (teamMembers || []).forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [teamMembers]);

  // Fetch list when dialog opens
  useEffect(() => {
    if (!open) return;
    setStage("select");
    setSelectedId(null);
    setFieldChoices({});
    setCustomFieldChoices({});
    let cancelled = false;
    (async () => {
      setLoadingList(true);
      try {
        if (presetSourceId) {
          // Direct merge from a specific lead — fetch that one lead and jump to resolve stage
          const res = await axios.get(`${API}/leads/${presetSourceId}`);
          if (!cancelled && res.data) {
            setCandidates([res.data]);
            setSelectedId(res.data.id);
            setStage("resolve");
          }
        } else {
          const res = await axios.get(`${API}/leads/engarage/unmatched?limit=20`);
          if (!cancelled) setCandidates(res.data?.leads || []);
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load merge candidates");
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, presetSourceId]);

  const selectedSource = candidates.find((c) => c.id === selectedId);

  // Compute conflicts when proceeding from SELECT -> RESOLVE
  const conflicts = useMemo(() => {
    if (!currentLead || !selectedSource) return { fields: [], customFields: [] };
    const fieldConflicts = [];
    for (const f of MERGEABLE_FIELDS) {
      const cv = currentLead[f];
      const sv = selectedSource[f];
      if (isEmpty(cv) && isEmpty(sv)) continue;
      if (cv === sv) continue;
      if (isEmpty(cv) && !isEmpty(sv)) continue; // auto-take source (no UI choice)
      if (isEmpty(sv) && !isEmpty(cv)) continue; // auto-keep current
      fieldConflicts.push({ field: f, current: cv, source: sv });
    }
    // Custom fields
    const curCF = currentLead.custom_fields || {};
    const srcCF = selectedSource.custom_fields || {};
    const cfConflicts = [];
    const allCFKeys = new Set([...Object.keys(curCF), ...Object.keys(srcCF)]);
    for (const k of allCFKeys) {
      const cv = curCF[k];
      const sv = srcCF[k];
      if (isEmpty(cv) && isEmpty(sv)) continue;
      if (cv === sv) continue;
      if (isEmpty(cv) || isEmpty(sv)) continue;
      cfConflicts.push({ key: k, current: cv, source: sv });
    }
    return { fields: fieldConflicts, customFields: cfConflicts };
  }, [currentLead, selectedSource]);

  // Initialize default choices when entering RESOLVE
  useEffect(() => {
    if (stage !== "resolve") return;
    const fc = {};
    conflicts.fields.forEach((c) => { fc[c.field] = "current"; });
    setFieldChoices(fc);
    const cfc = {};
    conflicts.customFields.forEach((c) => { cfc[c.key] = "current"; });
    setCustomFieldChoices(cfc);
  }, [stage, conflicts]);

  const handleProceedToResolve = () => {
    if (!selectedId) {
      toast.error("Select an ENGARAGE lead to merge");
      return;
    }
    setStage("resolve");
  };

  const handleProceedToConfirm = () => {
    setStage("confirm");
  };

  const handleConfirmMerge = async () => {
    setMerging(true);
    try {
      const res = await axios.post(`${API}/leads/${currentLead.id}/merge`, {
        source_lead_id: selectedId,
        field_choices: fieldChoices,
        custom_field_choices: customFieldChoices,
      });
      toast.success(res.data?.message || "Leads merged");
      onOpenChange(false);
      if (onMerged) onMerged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to merge leads");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !merging && onOpenChange(v)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="merge-lead-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Link2 className="w-5 h-5 text-teal-600" />
            Match ENGARAGE Account
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {stage === "select" && "Choose a recent ENGARAGE registration lead to merge into this lead."}
            {stage === "resolve" && "Resolve any conflicting data between the two leads."}
            {stage === "confirm" && "Final confirmation before merging."}
          </DialogDescription>
        </DialogHeader>

        {/* STAGE: SELECT */}
        {stage === "select" && (
          <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="merge-stage-select">
            {loadingList ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading ENGARAGE leads…
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Layers className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">No unmatched ENGARAGE leads found.</p>
              </div>
            ) : (
              <RadioGroup value={selectedId || ""} onValueChange={setSelectedId} className="space-y-2">
                {candidates.map((c) => {
                  const isSelf = c.id === currentLead?.id;
                  return (
                    <label
                      key={c.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelf
                          ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                          : selectedId === c.id
                          ? "border-teal-400 bg-teal-50/50 ring-1 ring-teal-300"
                          : "border-gray-200 hover:border-teal-300 hover:bg-gray-50"
                      }`}
                      data-testid={`merge-candidate-${c.id}`}
                    >
                      <RadioGroupItem value={c.id} disabled={isSelf} className="mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {c.name || "(no name)"}
                            {isSelf && <span className="ml-2 text-xs text-gray-400">(this lead)</span>}
                          </p>
                          <span className="text-xs text-gray-400 shrink-0">{formatCreatedAt(c.created_at)}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-600">
                          <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 text-gray-400" /> {c.email || "—"}</span>
                          <span className="flex items-center gap-1 truncate"><Phone className="w-3 h-3 text-gray-400" /> {c.phone || "—"}</span>
                          <span className="flex items-center gap-1 truncate"><Tag className="w-3 h-3 text-gray-400" /> {c.status}{c.subscription_plan ? ` · ${c.subscription_plan}` : ""}</span>
                          <span className="flex items-center gap-1 truncate"><UserCircle2 className="w-3 h-3 text-gray-400" /> {c.assigned_name || "Unassigned"}</span>
                        </div>
                        {c.custom_fields?.["Workshop ID"] && (
                          <p className="text-xs text-teal-700 mt-1">Workshop ID: {c.custom_fields["Workshop ID"]}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            )}
          </div>
        )}

        {/* STAGE: RESOLVE */}
        {stage === "resolve" && selectedSource && (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5" data-testid="merge-stage-resolve">
            {conflicts.fields.length === 0 && conflicts.customFields.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-700 font-medium">No conflicts found.</p>
                <p className="text-xs text-gray-500 mt-1">All fields will be merged automatically. Click Continue to review and confirm.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-[1fr,auto,1fr] gap-3 text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2 border-b border-gray-100">
                  <div className="pl-7">Current Lead</div>
                  <div className="w-6" />
                  <div className="pl-7">ENGARAGE Lead</div>
                </div>

                {conflicts.fields.map(({ field, current, source }) => {
                  const Icon = FIELD_META[field]?.icon || Tag;
                  const choice = fieldChoices[field] || "current";
                  return (
                    <div key={field} className="space-y-2" data-testid={`merge-field-${field}`}>
                      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                        <Icon className="w-3.5 h-3.5 text-gray-500" /> {FIELD_META[field]?.label || field}
                      </div>
                      <RadioGroup
                        value={choice}
                        onValueChange={(v) => setFieldChoices({ ...fieldChoices, [field]: v })}
                        className="grid grid-cols-[1fr,auto,1fr] gap-3"
                      >
                        <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${choice === "current" ? "border-blue-400 bg-blue-50/50" : "border-gray-200 hover:border-gray-300"}`}>
                          <RadioGroupItem value="current" className="mt-0.5" data-testid={`merge-field-${field}-current`} />
                          <span className="break-words">{displayValue(field, current, teamMap)}</span>
                        </label>
                        <div className="flex items-center justify-center text-gray-300">
                          <ChevronRight className="w-4 h-4" />
                        </div>
                        <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${choice === "source" ? "border-teal-400 bg-teal-50/50" : "border-gray-200 hover:border-gray-300"}`}>
                          <RadioGroupItem value="source" className="mt-0.5" data-testid={`merge-field-${field}-source`} />
                          <span className="break-words">{displayValue(field, source, teamMap)}</span>
                        </label>
                      </RadioGroup>
                    </div>
                  );
                })}

                {conflicts.customFields.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5" /> Custom Field Conflicts
                    </p>
                    {conflicts.customFields.map(({ key, current, source }) => {
                      const choice = customFieldChoices[key] || "current";
                      return (
                        <div key={key} className="space-y-2 mb-4" data-testid={`merge-cf-${key}`}>
                          <div className="text-xs font-medium text-gray-700">{key}</div>
                          <RadioGroup
                            value={choice}
                            onValueChange={(v) => setCustomFieldChoices({ ...customFieldChoices, [key]: v })}
                            className="grid grid-cols-[1fr,auto,1fr] gap-3"
                          >
                            <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${choice === "current" ? "border-blue-400 bg-blue-50/50" : "border-gray-200 hover:border-gray-300"}`}>
                              <RadioGroupItem value="current" className="mt-0.5" />
                              <span className="break-words">{displayValue("notes", current, teamMap)}</span>
                            </label>
                            <div className="flex items-center justify-center text-gray-300">
                              <ChevronRight className="w-4 h-4" />
                            </div>
                            <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${choice === "source" ? "border-teal-400 bg-teal-50/50" : "border-gray-200 hover:border-gray-300"}`}>
                              <RadioGroupItem value="source" className="mt-0.5" />
                              <span className="break-words">{displayValue("notes", source, teamMap)}</span>
                            </label>
                          </RadioGroup>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="text-xs text-gray-500 italic pt-2">
                  Non-conflicting custom fields, meetings, and activity history from the ENGARAGE lead will be merged into this lead automatically.
                </p>
              </>
            )}
          </div>
        )}

        {/* STAGE: CONFIRM */}
        {stage === "confirm" && selectedSource && (
          <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="merge-stage-confirm">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 space-y-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <h3 className="text-sm font-bold text-red-900 uppercase tracking-wide">
                    This action is irreversible
                  </h3>
                  <p className="text-sm text-red-800 leading-relaxed">
                    You are about to merge the ENGARAGE lead
                    {" "}<strong>{selectedSource.name}</strong>{" "}
                    (<span className="font-mono text-xs">{selectedSource.email}</span>) into the current lead
                    {" "}<strong>{currentLead.name}</strong>.
                  </p>
                  <ul className="text-xs text-red-800 space-y-1 list-disc pl-5">
                    <li>The ENGARAGE lead will be archived and removed from the leads list.</li>
                    <li>Its activity, meetings, and custom fields will be moved into this lead.</li>
                    <li>Your conflict choices will overwrite this lead's data and <strong>cannot be undone</strong>.</li>
                  </ul>
                  <p className="text-sm text-red-900 font-semibold pt-2">
                    Are you absolutely sure you want to proceed?
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between sm:justify-between gap-2">
          {stage !== "select" ? (
            <Button
              variant="ghost"
              onClick={() => setStage(stage === "confirm" ? "resolve" : "select")}
              disabled={merging}
              data-testid="merge-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
          ) : <div />}

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={merging}
              data-testid="merge-cancel-btn"
            >
              Cancel
            </Button>
            {stage === "select" && (
              <Button
                onClick={handleProceedToResolve}
                disabled={!selectedId || loadingList}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="merge-next-btn"
              >
                Continue
              </Button>
            )}
            {stage === "resolve" && (
              <Button
                onClick={handleProceedToConfirm}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="merge-review-btn"
              >
                Review & Merge
              </Button>
            )}
            {stage === "confirm" && (
              <Button
                onClick={handleConfirmMerge}
                disabled={merging}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="merge-confirm-btn"
              >
                {merging ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                Yes, merge
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
